import { Component, Input, OnInit } from '@angular/core';
import { GlobalFunctions } from '@app/Global/GlobalFunctions';
import { animate, style, transition, trigger } from '@angular/animations';
import { NotifyService } from '@app/Services/NotifyService';
import { MatDialog } from '@angular/material/dialog';
import { ApiService } from '@app/Services/APIService';
import { StaticDataControllerMethods } from '@app/Global/EnumManager';
import { ClientDataStore } from '@app/Global/ClientDataStore';

@Component({
    selector: 'ReleaseNotes',
    templateUrl: './ReleaseNotes.html',
    styleUrls: ['./ReleaseNotes.scss'],
    animations: [
        trigger('fadeInSection', [
            transition(':enter', [
                style({ opacity: '0' }),
                animate('1.0s ease-out', style({ opacity: '1' })),
            ]),
        ]),
    ]
})

export class ReleaseNotes implements OnInit {

    //Constructor
    constructor(public globalFunctions: GlobalFunctions,
        private notifyService: NotifyService,
        public dialog: MatDialog,
        private clientDataStore: ClientDataStore,
        private apiService: ApiService) {
    }

    //Unique identifier to the launched modal
    @Input() ModalIdentifier;

    //Default Release Note Type to blank, no selection yet
    public ReleaseNoteType: boolean = null;

    //List of lenders
    public LenderList = [];

    //Release note
    public ReleaseNotes: string;

    //Selected lenders options as per the primeng multiselect
    public SelectedLenders: any[] = [];

    //DateTime picker properties. Use boxing (using date as an object) to allow passing it by reference to convert it into ISO Format
    public DTPReleaseDate = { JSDate: null, ISODate: null };
    public DTPToDate = { JSDate: null, ISODate: null };
    public DTPMaxDate = new Date(2050, 1, 1);
    public DTPDateFormat = "dd/MM/yy";

    //Flag to track the status of selectAll checkbox on the primeng multiselect
    public LenderSelectAll = { Value: false };

    //Call type option: inbound or outbound
    public ReleaseNoteTypeOptions: any[] = [{ label: 'Yes', value: true }, { label: 'No', value: false }];

    ngOnInit(): void {

        //InitPage
        this.Page_Init();
    }


    //Setting defaults on initial load
    private Page_Init(): void {

        //Populate PrimeNG multi select Lender List
        this.MultiSelectLender_Get();
    }

    //Reset release date
    public ClearReleaseDate() {
        this.DTPReleaseDate = { JSDate: null, ISODate: null };
    }

    //Save release notes
    public SaveReleaseNotes() {

        //Validate the release note type selection
        if (this.ReleaseNoteType === null) {
            this.notifyService.Error_Show("Please select if it's an internal or a public note", "Error");
            return;
        }

        //Check if release note type is public then we need to validate if at least one lender is selected
        if (this.ReleaseNoteType == false) {
            //Validate the lender selection
            if (this.SelectedLenders.length == 0) {
                this.notifyService.Error_Show("Please select a lender", "Error");
                return;
            }
        }

        //Validate the release date selection
        if (this.globalFunctions.isEmpty(this.DTPReleaseDate.JSDate)) {
            this.notifyService.Error_Show("Please select release date", "Error");
            return;
        }

        //Validate the release note
        if (this.globalFunctions.isEmpty(this.ReleaseNotes)) {
            this.notifyService.Error_Show("Please enter release note", "Error");
            return;
        }

        //Let's call Server-side endpoint
        //Track state of this request, so that we can show the spinner, if necessary.
        this.clientDataStore.SetShowFullscreenLoading(true);

        //Construct the request and send it to the server
        const apiRequest = { IsInternal: this.ReleaseNoteType, SelectedLenders: this.SelectedLenders, ReleaseDate: this.DTPReleaseDate.JSDate, ReleaseNote: this.ReleaseNotes };

        //Let's call the endpoint to save release notes
        this.apiService.APIData_Post(this.apiService.Endpoints.StaticDataController, StaticDataControllerMethods[StaticDataControllerMethods.SaveReleaseNotes], apiRequest)
            .subscribe(apiResponse => {
                if (this.globalFunctions.isEmpty(apiResponse)) {

                    //There was no response, or an error.
                    //Track state of this request, so that we can show the spinner, if necessary.
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }

                else {
                    //Get the response, and try to display it in the document viewer
                    const response = JSON.parse(JSON.stringify(apiResponse));

                    //Check the response
                    if (!this.globalFunctions.isEmpty(response)) {
                        this.notifyService.Success_Show("The release note(s) have been saved successfully.", "Success");
                    }

                    //After saving, let's clear the fields
                    this.ReleaseNotes = "";
                    this.SelectedLenders.length = 0;
                    this.ReleaseNoteType = null;
                    this.DTPReleaseDate = { JSDate: null, ISODate: null };
                    this.clientDataStore.SetShowFullscreenLoading(false);

                    return;
                }
            });
    }

    //To clear release notes and start over again
    public ClearReleaseNotes() {

        //Clear contents
        this.ReleaseNotes = "";
    }

    //Hide the datetime picker panel
    public DTP_Close(calendar, date): void {
        this.globalFunctions.Date_ToISO(date);
        calendar.hideOverlay();
    }

    //Get all lenders from server using autocomplete
    public MultiSelectLender_Get(): void {

        //Construct an AutoComplete request body that we can post to the server
        const apiRequest = this.globalFunctions.AutoComplete_PrepareAPIRequest("Search", "Lender", "{seed}", "Lender");

        //Call the server, if we have some value to request
        this.apiService.APIData_Post(this.apiService.Endpoints.StaticDataController, apiRequest.AutoCompleteEndpoint, apiRequest)
            .subscribe(apiResponse => {
                if (apiResponse === null) { return; }
                else {

                    //Store the autocomplete data for the client to be passed into the API Request
                    const LenderMultiSelect = { Client_AutoCompleteControlData: [] };

                    //Processing response from server
                    this.globalFunctions.AutoComplete_ProcessResponse(apiResponse, LenderMultiSelect.Client_AutoCompleteControlData);

                    //Let's iterate through array Client_AutoCompleteControlData and add each item to PrimeNG multiselect array
                    LenderMultiSelect.Client_AutoCompleteControlData.forEach(item => {
                        this.LenderList.push({ ControlGUID: item.ControlGUID, ControlDisplay: item.ControlValue });
                    });
                }
            });
    }

    //Group selected items at the top
    public LenderMultiselect_Group(options, selectedItems): void {

        //Check for empty 
        if (!this.globalFunctions.isEmpty(options) && options.length > 0) {
            //Sort in ascending order
            options.sort((a, b) => a.ControlDisplay.localeCompare(b.ControlDisplay));
        }

        //Check for empty 
        if (!this.globalFunctions.isEmpty(selectedItems) && selectedItems.length > 0) {

            //Sort in descending order, since when grouping selected items, we want to add them in reverse order Z --> A, so that resultant array becomes sorted alphabetically (All selected items - in alphabetical order at the top, followed by un-selected items ordered alphabetically)
            selectedItems.sort((a, b) => b.ControlDisplay.localeCompare(a.ControlDisplay));

            //Loop through all the selected items and add those on the top of options
            selectedItems.forEach(item => {

                const selectedOption = options.filter(x => x.ControlGUID === item.ControlGUID)[0];

                if (!this.globalFunctions.isEmpty(selectedOption)) {

                    //Remove the selected item from the list
                    options.splice(options.indexOf(selectedOption), 1);

                    //Now add the same selected item on the top 
                    options.unshift(selectedOption);
                }
            })
        }
    }

    //Action when a calltype option is selected
    public ReleaseNoteTypeOption_Click(event): void {

        //Set the release note type value
        this.ReleaseNoteType = event.value;

        //TODO : Do we need to Sync anything here?

    }
}